import React from 'react';
import numeral from 'numeral';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import ParsedHtml from '@buyside/components/presentation/ParsedHtml/parsedHtml';
import TooltipWhite from '@buyside/components/presentation/Tooltips/TooltipWhite/tooltipWhite';
import ReturnValue from '@buyside/components/presentation/ReturnValue/returnValue';

import { useCryptoStatsQuery } from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/CompanyShowPage/companyShowPageStyles.scss';

const tooltipMap: { [key: string]: string } = {
  marketCap:
    "The total market value of a cryptocurrency's circulating supply. It is analogous to the free-float capitalization in the stock market.",
  fullyDilutedMarketCap: 'The market cap if the max supply was in circulation.',
  volume24h: 'A measure of how much of a cryptocurrency was traded in the last 24 hours.',
  circulatingSupply:
    'The amount of coins that are circulating in the market and are in public hands. It is analogous to the flowing shares in the stock market.',
  maxSupply:
    'The maximum amount of coins that will ever exist in the lifetime of the cryptocurrency. It is analogous to the fully diluted shares in the stock market.',
  totalSupply:
    'The amount of coins that have been already created, minus any coins that have been burned. It is analogous to the outstanding shares in the stock market.'
};

const EntityCryptoStats: React.FC<{ id: string }> = ({ id }): React.ReactElement => {
  const { data, loading, error } = useCryptoStatsQuery({ variables: { id } });

  if (loading) return <LoadingSpinner size={SIZES.MEDIUM} />;

  if (error) throw error;

  if (
    !data?.entity?.primaryAsset?.companyData ||
    !data?.entity?.primaryAsset?.currencyCode ||
    !data?.entity?.primaryAsset?.cryptoMarketData
  ) {
    return (
      <div className={styles.accordionInner}>
        <span className={styles.smallText}>No data</span>
      </div>
    );
  }

  const {
    companyData: { country, geography, industry, sector },
    currencyCode,
    fiftyTwoWeekRange,
    cryptoMarketData: {
      circulatingSupply,
      fullyDilutedMarketCap,
      marketCap,
      maxSupply,
      percentChange24h,
      percentChange7d,
      totalSupply,
      volume24h
    }
  } = data.entity?.primaryAsset;

  const summary = [
    { label: 'Sector', value: sector },
    { label: 'Industry', value: industry },
    { label: 'Country', value: country },
    { label: 'Region', value: geography }
  ];

  const stats = [
    { label: 'Currency', value: currencyCode, name: 'currencyCode' },
    {
      label: `52 Wk. Range  ${currencyCode ? `(${currencyCode})` : ''}`,
      value: fiftyTwoWeekRange,
      name: 'fiftyTwoWeekRange'
    },
    {
      label: '24hr  %',
      value: percentChange24h ? <ReturnValue value={percentChange24h} /> : '-',
      name: 'percentChange24h'
    },
    {
      label: '7d  %',
      value: percentChange7d ? <ReturnValue value={percentChange7d} /> : '-',
      name: 'percentChange7d'
    },
    {
      label: 'Market Cap',
      value: `${numeral(marketCap).format('0.0a')} ${currencyCode}`,
      name: 'marketCap'
    },
    {
      label: 'Fully Diluted Market Cap',
      value: `${numeral(fullyDilutedMarketCap).format('0.0a')} ${currencyCode}`,
      name: 'fullyDilutedMarketCap'
    },
    {
      label: 'Volume  (24hr)',
      value: `${numeral(volume24h).format('0.0a')} ${currencyCode}`,
      name: 'volume24h'
    },
    {
      label: 'Circulating Supply',
      value: numeral(circulatingSupply).format('0.0a'),
      name: 'circulatingSupply'
    },
    {
      label: 'Max Supply',
      value: maxSupply ? numeral(maxSupply).format('0.0a') : '-',
      name: 'maxSupply'
    },
    {
      label: 'Total Supply',
      value: totalSupply ? numeral(totalSupply).format('0.0a') : '-',
      name: 'totalSupply'
    }
  ];

  return (
    <div className={styles.stats}>
      <h5>Summary</h5>
      {summary.map(summaryStat => (
        <div key={summaryStat.label}>
          <span>{summaryStat.label}</span>
          <span>{summaryStat.value || '-'}</span>
        </div>
      ))}
      <h5>Crypto Market Data</h5>
      {stats.map(cryptoStat =>
        tooltipMap[cryptoStat.name] ? (
          <div key={cryptoStat.label}>
            <TooltipWhite
              overlay={
                <ParsedHtml html={tooltipMap[cryptoStat.name]}>
                  {({ content }): React.ReactNode => <div>{content}</div>}
                </ParsedHtml>
              }
              placement="right"
            >
              <div className={styles.tooltipStat}>
                <span>{cryptoStat.label}</span>
                <FontAwesomeIcon icon={faInfoCircle} />
              </div>
            </TooltipWhite>
            <span>{cryptoStat.value || '-'}</span>
          </div>
        ) : (
          <div key={cryptoStat.name}>
            <span>{cryptoStat.label}</span>
            <span>{cryptoStat.value || '-'}</span>
          </div>
        )
      )}
    </div>
  );
};

export default EntityCryptoStats;
